import type { Client } from '@/client';
import { isMobile } from '@/utils';

const DESKTOP_GAME_ZOOM = 2;
const MOBILE_GAME_ZOOM = 1.5;

export class ViewportController {
  private width = 800;
  private height = 600;
  private halfWidth = 400;
  private halfHeight = 300;
  private zoom = DESKTOP_GAME_ZOOM;
  private mobile = false;

  constructor(private client: Client) {
    let resizeRaf = 0;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => this.resizeCanvases());
    });
    this.client.configController.subscribe('gameZoom', () =>
      this.resizeCanvases(),
    );

    this.resizeCanvases();
  }

  setGameSize(w: number, h: number) {
    this.width = w;
    this.height = h;
    this.halfWidth = w >> 1;
    this.halfHeight = h >> 1;
  }

  getGameWidth() {
    return this.width;
  }

  getGameHeight() {
    return this.height;
  }

  getHalfGameWidth() {
    return this.halfWidth;
  }

  getHalfGameHeight() {
    return this.halfHeight;
  }

  isMobile() {
    return this.mobile;
  }

  setZoom() {
    this.mobile = isMobile();
    const baseZoom = this.mobile ? MOBILE_GAME_ZOOM : DESKTOP_GAME_ZOOM;
    const userZoom = this.client.configController.gameZoom || 1;
    this.zoom = Math.max(0.5, Math.min(3, baseZoom * userZoom));
  }

  zoomReset() {
    this.resizeCanvases();
  }

  zoomIn() {
    this.resizeCanvases();
  }

  zoomOut() {
    this.resizeCanvases();
  }

  resizeCanvases() {
    const viewportWidth = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0,
    );
    const viewportHeight = Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0,
    );

    this.setZoom();
    const w = Math.floor(viewportWidth / this.zoom);
    const h = Math.floor(viewportHeight / this.zoom);

    // Resize the PixiJS renderer canvas if it's been initialised
    const renderer = this.client.app?.renderer;
    if (renderer?.canvas) {
      const canvas = renderer.canvas;
      canvas.style.width = `${w * this.zoom}px`;
      canvas.style.height = `${h * this.zoom}px`;
      renderer.resize(w, h);
    }

    this.setGameSize(w, h);
  }
}
